/**
 * Pure rules of the "What's new" dialog (#1355): language fallback, grouping
 * by the viewer's local day, and when the dialog opens on its own.
 */

import type { ChangelogEntry, LocalizedText } from '@/services/changelog-service'
import type { ChangelogSettings } from '@/types/settings'

export function localizedText(text: LocalizedText | undefined, lang: string): string {
  if (!text) return ''
  const primary = lang.startsWith('de') ? text.de : text.en
  return primary || text.en || text.de || ''
}

export interface ChangelogDay {
  /** Local calendar day, `YYYY-MM-DD`. */
  day: string
  /** Any instant of that day, for formatting the heading. */
  date: Date
  entries: ChangelogEntry[]
}

function localDayKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** Entries arrive newest first; the days and the entries in them keep that order. */
export function groupByLocalDay(entries: ChangelogEntry[]): ChangelogDay[] {
  const days: ChangelogDay[] = []
  for (const entry of entries) {
    const date = new Date(entry.publishedAt)
    const day = localDayKey(date)
    const last = days[days.length - 1]
    if (last && last.day === day) {
      last.entries.push(entry)
    } else {
      days.push({ day, date, entries: [entry] })
    }
  }
  return days
}

const UTC_INSTANT = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?Z$/

/** `…T09:12:03.1Z` → `…T09:12:03.100000`, so two UTC instants compare as strings. */
function microsecondKey(value: string): string | null {
  const match = UTC_INSTANT.exec(value)
  if (!match) return null
  return `${match[1]}.${(match[2] ?? '').padEnd(6, '0')}`
}

/**
 * The server sends microseconds; `Date` keeps only milliseconds, and two
 * entries of one sync run can share a millisecond. Exact when both values are
 * UTC instants as the API writes them, `Date` otherwise.
 */
export function isNewerThan(publishedAt: string, lastSeenAt: string): boolean {
  const publishedKey = microsecondKey(publishedAt)
  const seenKey = microsecondKey(lastSeenAt)
  if (publishedKey && seenKey) return publishedKey > seenKey
  const published = Date.parse(publishedAt)
  const seen = Date.parse(lastSeenAt)
  if (Number.isNaN(published)) return false
  if (Number.isNaN(seen)) return true
  return published > seen
}

/**
 * What the desktop does at start, before anything is fetched:
 * - `skip`: the person turned the dialog off.
 * - `initialize`: no `lastSeenAt` yet — a first login or a person who last
 *   saw the version-based changelog. Their baseline is "now"; a first login
 *   is not "what's new", and the migrated ones already saw the old list.
 * - `check`: compare the newest entry with `lastSeenAt`.
 */
export type StartupAction = 'skip' | 'initialize' | 'check'

export function startupAction(changelog: Partial<ChangelogSettings> | undefined): StartupAction {
  if (changelog?.hideOnStartup) return 'skip'
  if (!changelog?.lastSeenAt) return 'initialize'
  return 'check'
}

/**
 * The marker written when the dialog closes: the newest entry it showed, in
 * server time, so a skewed client clock cannot hide or repeat an entry. Never
 * moves backwards.
 */
export function nextLastSeenAt(previous: string | undefined, newestShown: string | undefined): string | undefined {
  if (!newestShown) return previous
  if (!previous || isNewerThan(newestShown, previous)) return newestShown
  return previous
}
